<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 checkbox with label, error state, and indeterminate support.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Selecting one or more independent boolean options; use HrOptionGroup for data-driven lists
 * @related HrOptionGroup
 * @see https://quasar.dev/vue-components/checkbox Quasar q-checkbox API
 */
defineOptions({ name: 'HrCheckbox' });

/**
 * @typedef {'left' | 'right'} HRCheckboxLabelPosition
 */
const props = defineProps({
  /**
   * Current value; when bound to an array the checkbox uses array-mode and `val` is checked against the array.
   */
  modelValue: {
    type: null,
    default: false,
  },
  /**
   * Value contributed when modelValue is an array. Enables array-mode binding.
   */
  val: {
    type: null,
    default: null,
  },
  /** Value emitted when checked (non-array mode). */
  trueValue: {
    type: null,
    default: true,
  },
  /** Value emitted when unchecked (non-array mode). */
  falseValue: {
    type: null,
    default: false,
  },
  /** Value representing the indeterminate state. */
  indeterminateValue: {
    type: null,
    default: null,
  },
  /** When true, clicking cycles through checked → unchecked → indeterminate. */
  toggleIndeterminate: {
    type: Boolean,
    default: false,
  },
  /** Label text shown beside the checkbox. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Position of the label relative to the checkbox.
   * @values left, right
   */
  labelPosition: {
    type: String,
    default: 'right',
    validator: (v) => ['left', 'right'].includes(v),
  },
  /** Prevents interaction and dims the checkbox. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Switches the color role to error. Use to indicate invalid selection state.
   */
  error: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props are any additional Quasar props that can be passed to the checkbox for backwards
   * compatibility with the legacy theme. These props will be ignored when a design system theme
   * is active.
   * @type {Object} legacyProps
   * @deprecated This prop is only for backwards compatibility with the legacy theme and should be
   * removed once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the checked state changes; payload is the new value. */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => [
  'hr-checkbox',
  props.error && 'hr-checkbox--error',
]);

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    ...(props.val !== null && { val: props.val }),
    trueValue: props.trueValue,
    falseValue: props.falseValue,
    indeterminateValue: props.indeterminateValue,
    toggleIndeterminate: props.toggleIndeterminate,
    ...(props.label !== null && { label: props.label }),
    leftLabel: props.labelPosition === 'left',
    disable: props.disable,
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-checkbox
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <!-- @slot default Custom label content; use instead of the `label` prop for rich content. -->
    <slot />
  </q-checkbox>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-checkbox {
  --hr-cb-box:   18px;
  --hr-cb-layer: 40px;
}

/* ── Inner container ─────────────────────────────────────────────────── */
/*
 * font-size: 1px neutralises Quasar's em-based proportional sizing.
 * color drives currentColor throughout: box border (border: 2px solid currentColor),
 * box fill (background: currentColor on truthy/indet), and the state layer
 * (::before background: currentColor). Setting it here fixes everything in one place.
 */
.hr-checkbox :deep(.q-checkbox__inner) {
  font-size: 1px;
  width: var(--hr-cb-layer);
  height: var(--hr-cb-layer);
  color: var(--app-on-surface-variant);
}

/* Override Quasar's hardcoded --q-primary */
.hr-checkbox :deep(.q-checkbox__inner--truthy),
.hr-checkbox :deep(.q-checkbox__inner--indet) {
  color: var(--app-primary);
}

/* ── Box (18dp visual container) ─────────────────────────────────────── */
.hr-checkbox :deep(.q-checkbox__bg) {
  top:    calc((var(--hr-cb-layer) - var(--hr-cb-box)) / 2);
  left:   calc((var(--hr-cb-layer) - var(--hr-cb-box)) / 2);
  width:  var(--hr-cb-box);
  height: var(--hr-cb-box);
  border-radius: 2px;
  transition: background 150ms cubic-bezier(0.4, 0, 0.2, 1),
              border-color 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* M3: filled with primary, no visible border */
.hr-checkbox :deep(.q-checkbox__inner--truthy .q-checkbox__bg),
.hr-checkbox :deep(.q-checkbox__inner--indet .q-checkbox__bg) {
  background: var(--app-primary);
  border-color: transparent;
}

/* ── State layer ─────────────────────────────────────────────────────── */
/*
 * Quasar's ::before uses background: currentColor — now correct via the inner
 * color fix above. Only adjustments needed: reduce hover opacity to M3's 8%
 * (Quasar defaults to 12%), and use on-surface for unchecked (on-surface-variant
 * drives the border but the state layer spec calls for on-surface).
 * body.desktop mirrors Quasar's own selector to win on specificity.
 */
/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-checkbox:not(.disabled):hover :deep(.q-checkbox__inner::before) {
  opacity: 0.08;
}

/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-checkbox:not(.disabled):hover :deep(.q-checkbox__inner--falsy::before),
/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-checkbox:not(.disabled):focus-within :deep(.q-checkbox__inner--falsy::before) {
  background: var(--app-on-surface);
}

/* ── Label ───────────────────────────────────────────────────────────── */
.hr-checkbox :deep(.q-checkbox__label) {
  padding-left: 8px;
  color: var(--app-on-surface);
}

.hr-checkbox.reverse :deep(.q-checkbox__label) {
  padding-left: 0;
  padding-right: 8px;
}

/* ── Error ───────────────────────────────────────────────────────────── */
/*
 * Set color on all inner states so currentColor cascades to border, fill,
 * and state layer without further overrides.
 */
.hr-checkbox--error :deep(.q-checkbox__inner),
.hr-checkbox--error :deep(.q-checkbox__inner--truthy),
.hr-checkbox--error :deep(.q-checkbox__inner--indet) {
  color: var(--app-error);
}

.hr-checkbox--error :deep(.q-checkbox__inner--truthy .q-checkbox__bg),
.hr-checkbox--error :deep(.q-checkbox__inner--indet .q-checkbox__bg) {
  background: var(--app-error);
}

/* Override Quasar's hardcoded #fff on the SVG with on-error token */
.hr-checkbox--error :deep(.q-checkbox__inner--truthy .q-checkbox__svg),
.hr-checkbox--error :deep(.q-checkbox__inner--indet .q-checkbox__svg) {
  color: var(--app-on-error);
}

/* ── Disabled ────────────────────────────────────────────────────────── */
/* Override Quasar's blanket opacity: 0.75 !important */
.hr-checkbox.disabled {
  opacity: 1 !important;
}

/* Single color override cascades to border, fill, and state layer */
.hr-checkbox.disabled :deep(.q-checkbox__inner) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

.hr-checkbox.disabled :deep(.q-checkbox__inner--truthy .q-checkbox__bg),
.hr-checkbox.disabled :deep(.q-checkbox__inner--indet .q-checkbox__bg) {
  background: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

/* Override Quasar's hardcoded #fff — checked mark should use surface on disabled */
.hr-checkbox.disabled :deep(.q-checkbox__inner--truthy .q-checkbox__svg),
.hr-checkbox.disabled :deep(.q-checkbox__inner--indet .q-checkbox__svg) {
  color: var(--app-surface);
}

.hr-checkbox.disabled :deep(.q-checkbox__label) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

</style>
